import React, { useContext, useEffect, useState } from "react";

import DialogBox from "../modals/DialogBox";
import {
  deleteProject,
  removeMemberFromProject,
  updateProject,
} from "../../api/Api";
import { showToast } from "../../utils/toastUtils";
import { useNavigate } from "react-router-dom";
import { ProjectContext } from "../../contexts/ProjectContextProvider";
import { useFormik } from "formik";
import { ProjectUpdateSchema } from "../../utils/validations";
import styles from "./ProjectSettingsForm.module.css";
import DangerCard from "../ui/DangerCard";
import Box from "../ui/Box";
import { MemberListCard } from "../../pages/Organization";
import AddNewMember from "../modals/AddNewMember";
import CustomButton from "../CustomButton";
import ShortTextInput from "../Inputs/ShortTextInput";
import { ProjectBudget } from "../../pages/Project";
import TextAreaInput from "../Inputs/TextAreaInput";
import { useUser } from "../../contexts/UserContextProvider";

const ProjectSettingsForm = ({
  project,
  loading,
  projectId,
  refreshInitiative,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [start_date, setStartDate] = useState("");
  const [end_date, setEndDate] = useState("");
  const { refresh } = useContext(ProjectContext);
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const [openDeletePopup, setOpenDeletePopup] = useState(false); // for delete project
  const [openLeave, setOpenLeave] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState({
    open: false,
    userId: null,
  });

  const formik = useFormik({
    initialValues: {
      name: name || "",
      description: description || "",
      start_date: start_date || "",
      end_date: end_date || "",
    },
    validationSchema: ProjectUpdateSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        name: values.name,
        description: values.description,
        start_date: values.start_date,
        end_date: values.end_date,
      };

      // project data updating function
      updateProject(projectId, payload)
        .then((response) => {
          if (response.status === 200) {
            refreshInitiative();
            refresh();
            console.log("Project updated successfully");
            showToast("Project updated successfully", "success");
          }
        })
        .catch((error) => {
          showToast(
            error.response?.data?.message || "Error loading data",
            "error"
          );
          console.log(error);
        });
    },
  });

  // reset form values
  const resetFormValues = () => {
    formik.resetForm();
    setName(project.name);
    setDescription(project.description);
    setStartDate(project.start_date);
    setEndDate(project.end_date);
  };

  // get project
  useEffect(() => {
    console.log(project);
    if (project) {
      setName(project.name);
      setDescription(project.description);
      setStartDate(project.start_date);
      setEndDate(project.end_date);
    }
  }, [project]);

  // delete project function
  const handleDelete = () => {
    deleteProject(projectId).then((response) => {
      if (response.status === 200) {
        refresh();
        navigate("/home");
        showToast("Project deleted successfully", "success");
      }
    });
  };

  // function to remove member
  const handleRemoveMember = (user_id, projectId) => {
    console.log(user_id, projectId);
    // Using userInfo from context.
    if (user_id === userInfo?.id) {
      setOpenLeave(true);
      return;
    }

    removeMemberFromProject(projectId, user_id)
      .then((response) => {
        if (response.status === 200) {
          showToast("Member removed successfully", "success");
          refreshInitiative();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const closeAlertLeave = () => setOpenLeave(false);
  const handleLeaveProject = () => {
    // Using userInfo from context.
    removeMemberFromProject(projectId, userInfo?.id)
      .then((response) => {
        if (response.status === 200) {
          showToast("Left project successfully", "success");
          refresh();
          navigate("/home");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const owner =
    project?.owner?.first_name && project?.owner?.last_name
      ? `${project.owner.first_name} ${project.owner.last_name}`
      : "Not Available";

  return (
    <>
      <Box className="bg-white">
        <Box
          className="secondary-box rounded-4"
          innerClass="d-flex flex-column flex-lg-row gap-3 align-items-start"
        >
          <Box className="secondary-box rounded-4 bg-white">
            <form className="mt-3" onSubmit={formik.handleSubmit}>
              {/* form fields */}
              {/* name */}
              <div className="d-flex align-items-baseline">
                <p className="p3 " style={{ minWidth: "150px" }}>
                  Name
                </p>
                <ShortTextInput
                  id="name"
                  style={{ minWidth: "300px", maxwidth: "400px" }}
                  className="text-secondary"
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  error={formik.errors.name}
                />
              </div>

              {/* Budget */}
              {/* <div className="d-flex align-items-baseline">
                <p className="p3 " style={{ minWidth: "150px" }}>
                  Budget
                </p>
                <ShortTextInput
                  id="name"
                  style={{ minWidth: "300px", maxwidth: "400px" }}
                  className="text-secondary"
                  onChange={formik.handleChange}
                  value={10000}
                  disabled
                  error={formik.errors.name}
                />
              </div>*/}

              {/* owner */}
              <div className="d-flex align-items-center">
                <p className="p3" style={{ minWidth: "150px" }}>
                  Owner
                </p>
                <ShortTextInput
                  style={{ minWidth: "300px", maxwidth: "400px" }}
                  className="text-secondary"
                  value={owner}
                  disabled
                />
              </div>

              {/* description */}
              <div className="d-flex w-100">
                <p className="p3 mt-3" style={{ minWidth: "150px" }}>
                  Description
                </p>
                <TextAreaInput
                  id="description"
                  className="text-secondary"
                  onChange={formik.handleChange}
                  value={formik.values.description}
                  error={formik.errors.description}
                  fullWidth
                />
              </div>

              {/* Start Date */}
              <div className="d-flex w-100">
                <p className="p3 mt-3" style={{ minWidth: "150px" }}>
                  Start Date
                </p>
                <ShortTextInput
                  id="start_date"
                  name="start_date"
                  type="date"
                  value={formik.values.start_date}
                  onChange={formik.handleChange}
                  error={formik.touched.start_date && formik.errors.start_date}
                />
              </div>
              {/* End Date */}
              <div className="d-flex w-100">
                <p className="p3 mt-3" style={{ minWidth: "150px" }}>
                  End Date
                </p>
                <ShortTextInput
                  id="end_date"
                  name="end_date"
                  type="date"
                  value={formik.values.end_date}
                  onChange={formik.handleChange}
                  error={formik.touched.end_date && formik.errors.end_date}
                />
              </div>

              {/* buttons */}
              <div style={{ marginLeft: "150px" }} className="d-flex mt-3">
                <CustomButton
                  variant="outline"
                  className="px-5"
                  type="button"
                  onClick={resetFormValues}
                >
                  Reset
                </CustomButton>
                <CustomButton
                  className="ms-2 px-5"
                  onClick={formik.handleSubmit}
                  type="submit"
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  Save
                </CustomButton>
              </div>
            </form>
          </Box>

          {/* project budget card */}
          {/*<ProjectBudget nonAllocated={3000} spent={2700} remaining={4300} /> */}
        </Box>
      </Box>

      {/* member list */}
      <Box>
        <MemberListCard
          id={projectId}
          members={
            project?.members?.map((member) => {
              if (member.id === userInfo?.id) {
                // Append (you) to the role if not already present
                const RoleWithYou = member.role?.toLowerCase().includes("(you)")
                  ? member.role
                  : `${member.role} (you)`;
                return { ...member, role: RoleWithYou };
              }
              return member;
            })
          }
          loading={loading}
          headerTitle="Project Members"
          headerButtonTitle="Add Member"
          onButtonClick={() => setShowAddMember(true)}
          onDelete={(user_id) => {
            // Using userInfo from context.
            if (user_id === userInfo?.id) {
              setOpenLeave(true);
            } else {
              setConfirmRemove({ open: true, userId: user_id });
            }
          }}
        />
      </Box>

      {/* danger zone*/}
      <Box>
        <DangerCard
          onClick={() => setOpenDeletePopup(true)}
          buttonTitle="Delete Project"
          bulletPoint2={
            <div>
              Deleting this <strong>Project will permanently remove</strong> all
              associated data, including workstreams, papers, and supplementary
              materials.
            </div>
          }
        />
      </Box>

      {/* delete project popup */}
      <DialogBox
        open={openDeletePopup}
        setClose={() => setOpenDeletePopup(false)}
        bodyText={
          <span>
            Once deleted, this{" "}
            <strong>project will be permanently removed </strong> and cannot be
            recovered. Are you sure you want to delete?
          </span>
        }
        submitButtonTitle="Delete"
        submitButtonVariant="btn-danger"
        onClick={handleDelete}
      />

      <DialogBox
        open={openLeave}
        setClose={closeAlertLeave}
        bodyText={<span>Do you want to leave this project?</span>}
        submitButtonTitle="Yes"
        submitButtonVariant="btn-danger"
        onClick={handleLeaveProject}
      />
      <DialogBox
        open={confirmRemove.open}
        setClose={() => setConfirmRemove({ open: false, userId: null })}
        bodyText="Are you sure you want to remove this member from the project?"
        submitButtonTitle="Remove"
        submitButtonVariant="btn-danger"
        onClick={() => {
          handleRemoveMember(confirmRemove.userId, projectId);
          setConfirmRemove({ open: false, userId: null });
        }}
      />

      {/* add member popup */}
      <AddNewMember
        show={showAddMember}
        handleClose={() => setShowAddMember(false)}
        project_id={projectId}
      />
    </>
  );
};
export default ProjectSettingsForm;
